'use client';

import React, { useEffect, useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import { getInvoices } from '@/lib/services/portalService';

interface ClientInvoiceSummaryProps {
  clientId?: string;
}

export default function ClientInvoiceSummary({ clientId }: ClientInvoiceSummaryProps) {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) {
      setLoading(false);
      return;
    }
    getInvoices(clientId).then((data) => {
      setInvoices(data || []);
      setLoading(false);
    });
  }, [clientId]);

  const totalBilled = invoices.reduce((sum, inv) => sum + Number(inv.total), 0);
  const totalPaid = invoices.filter((inv) => inv.status === 'paid').reduce((sum, inv) => sum + Number(inv.total), 0);
  const outstanding = totalBilled - totalPaid;

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR', maximumFractionDigits: 0 }).format(val);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="card">
      <div className="px-5 pt-5 pb-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-foreground">Invoice Summary</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{invoices.length} invoices total</p>
          </div>
          <a href="/invoices" className="btn-secondary text-xs px-3 py-1.5">View All</a>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 divide-x divide-border border-b border-border">
        {[
          { label: 'Total Billed', value: formatCurrency(totalBilled) },
          { label: 'Total Paid', value: formatCurrency(totalPaid) },
          { label: 'Outstanding', value: formatCurrency(outstanding) },
        ].map((stat) => (
          <div key={stat.label} className="px-5 py-4 text-center">
            <p className="text-xs text-muted-foreground">{stat.label}</p>
            <p className="text-base font-bold text-foreground mt-0.5">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Invoice list */}
      <div className="divide-y divide-border">
        {loading ? (
          <div className="py-8 text-center">
            <svg className="w-5 h-5 animate-spin text-primary mx-auto" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : invoices.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground">No invoices yet.</p>
          </div>
        ) : (
          invoices.slice(0, 4).map((inv) => (
            <div key={inv.id} className="px-5 py-3.5 flex items-center justify-between gap-3 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">{inv.invoice_number}</p>
                  <p className="text-xs text-muted-foreground">Due {formatDate(inv.due_date)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-sm font-bold text-foreground">{formatCurrency(Number(inv.total))}</span>
                <StatusBadge status={inv.status} size="sm" />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}